import { getTenant } from "./tenantResolver";

/**
 * Stav v prehliadaci, ktory patri JEDNEJ prevadzke.
 *
 * Doteraz bol kazdy odberatel na vlastnej subdomene, takze sessionStorage bol
 * izolovany samotnym prehliadacom. Na jednej domene rozvozy.sk by token
 * z /demo prezil prechod na /veteran -- preto sa kluce skladaju s prevadzkou:
 *
 *   authToken@t:veteran        prevadzka podla slugu (path mode)
 *   authToken@h:palermo.sk     prevadzka na vlastnej domene (host mode)
 *   authToken@platform         platformova sprava (/sprava)
 *
 * site_gate_password a cookie_consent zostavaju zamerne globalne.
 *
 * Kazdy pristup je v try/catch: v sukromnom rezime Safari aj pri zablokovanych
 * datach stranky moze storage vyhodit vynimku a appka musi fungovat dalej.
 *
 * TRVALE PRIHLASENIE (R18):
 *   Zakaznik si moze dat "zapamataj si ma" a jeho token potom plati 30 dni,
 *   takze musi prezit zatvorenie prehliadaca -- na to sessionStorage nestaci
 *   a pouzije sa localStorage. Rozhoduje o tom SERVER (`persistent` v odpovedi
 *   na prihlasenie), nie klient: obsluhe sa dlhy token nevyhovie, aj keby si oň
 *   povedala, lebo na jej obrazovkach su mena, adresy a saldo vsetkych zakaznikov.
 *   Kluce su rovnake, lisi sa len uloziste; citanie skusi obe.
 */

const LEGACY_TOKEN_KEY = "authToken";
const LEGACY_PENDING_KEY = "pendingUsername";

export function scopeFor(tenant) {
  if (tenant.mode === "platform") return "platform";
  if (tenant.mode === "path") return `t:${tenant.slug}`;
  if (tenant.mode === "host") return `h:${tenant.hostname}`;
  return "root";
}

export const currentScope = () => scopeFor(getTenant());

const kluc = (nazov, scope = currentScope()) => `${nazov}@${scope}`;

function citaj(key) {
  // Najprv relacne uloziste: ked sa niekto na spolocnom pocitaci prihlasi
  // bez "zapamataj si ma", jeho token ma prednost pred trvalym.
  try {
    const zRelacie = window.sessionStorage.getItem(key);
    if (zRelacie !== null) return zRelacie;
  } catch {
    /* pokracuj na trvale uloziste */
  }
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Zapise hodnotu do zvoleneho uloziska a z toho druheho ju ODSTRANI --
 * inak by po odhlaseni a novom prihlaseni ostal v druhom ulozisku visiet
 * stary token a `citaj` by ho niekedy nasiel.
 */
function zapis(key, value, trvale = false) {
  const zmaz = (uloziste) => {
    try { uloziste.removeItem(key); } catch { /* nedostupne */ }
  };

  if (value === null || value === undefined) {
    try { zmaz(window.sessionStorage); } catch { /* nedostupne */ }
    try { zmaz(window.localStorage); } catch { /* nedostupne */ }
    return;
  }

  const ciel = trvale ? 'localStorage' : 'sessionStorage';
  const druhe = trvale ? 'sessionStorage' : 'localStorage';
  try {
    window[ciel].setItem(key, value);
  } catch {
    /* storage nedostupny -- pouzivatel sa bude musiet prihlasit znova */
  }
  try { zmaz(window[druhe]); } catch { /* nedostupne */ }
}

/**
 * Jednorazove prevzatie starého globalneho tokenu -- LEN na vlastnej domene
 * (host mode), kde sa origin nemeni (veteran.rozvozy.sk pred 301). Vodic
 * prihlaseny pocas nasadenia tak nestrati session. Na zdielanej domene sa
 * cudzi globalny token nikdy neprevezme.
 */
function prevezmiLegacy() {
  if (getTenant().mode !== "host") return;
  const stary = citaj(LEGACY_TOKEN_KEY);
  if (stary && !citaj(kluc("authToken"))) zapis(kluc("authToken"), stary);
  zapis(LEGACY_TOKEN_KEY, null);
  zapis(LEGACY_PENDING_KEY, null);
}

export function getAuthToken() {
  prevezmiLegacy();
  return citaj(kluc("authToken"));
}

/** `trvale = true` ulozi token tak, aby prezil zatvorenie prehliadaca (R18). */
export const setAuthToken = (token, trvale = false) => zapis(kluc("authToken"), token, trvale);
export const clearAuthToken = () => zapis(kluc("authToken"), null);

/** Token pre INU prevadzku -- platformova sprava pred prechodom na /<slug> (act-as). */
export const setAuthTokenForSlug = (slug, token) =>
  zapis(kluc("authToken", scopeFor({ mode: "path", slug })), token);

export const getPendingUsername = () => citaj(kluc("pendingUsername"));
export const setPendingUsername = (username) => zapis(kluc("pendingUsername"), username);
export const clearPendingUsername = () => zapis(kluc("pendingUsername"), null);

/**
 * Rozpracovany kosik na /objednavka: `{ den, pocty, poznamka }`.
 *
 * Bez neho sa kosik stracal pri obnoveni stranky a hlavne pri "Prihlasit sa
 * a objednat" -- neprihlaseneho to posle na registraciu a spat sa vracia uz
 * na prazdnu objednavku. Relacne uloziste staci: ma prezit navigaciu
 * a obnovenie v tej istej karte, nie zatvorenie prehliadaca. Co z neho
 * ostalo platne, posudi az ObjednavkaPage podla aktualnej ponuky.
 */
export function getKosik() {
  try {
    return JSON.parse(citaj(kluc("kosik")));
  } catch {
    return null;
  }
}
export const setKosik = (kosik) => zapis(kluc("kosik"), kosik ? JSON.stringify(kosik) : null);
